import { byId, isAr, trackEvent, attachCopyButton, setupSegmented, formatNumber } from './calc-common.js';
import Big from 'big.js';

let currentMode = 'part_of_whole';

function calculate() {
  const resultValEl = byId('pct-result-val');
  const resultLabelEl = byId('pct-result-label');
  const resultSubEl = byId('pct-result-sub');
  const formulaEl = byId('pct-formula');
  const breakdownWrap = byId('pct-breakdown-wrap');

  try {
    if (currentMode === 'part_of_whole') {
      const x = Number(byId('pct-x')?.value || 0);
      const y = Number(byId('pct-y')?.value || 0);
      const res = new Big(x).div(100).times(y);

      if (resultLabelEl) resultLabelEl.textContent = isAr ? `نسبة ${x}% من ${y}` : `${x}% of ${y}`;
      if (resultValEl) resultValEl.textContent = formatNumber(res.toNumber(), 4);
      if (resultSubEl) resultSubEl.textContent = isAr ? `الناتج النهائي بدقة` : `Final calculated amount`;
      if (formulaEl) formulaEl.textContent = `(${x} ÷ 100) × ${y} = ${res.toString()}`;
      if (breakdownWrap) {
        breakdownWrap.innerHTML = `
          <div class="kz-breakdown-row"><span>${isAr ? 'النسبة المئوية المطلوبة:' : 'Percentage:'}</span><strong>${x}%</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'المبلغ أو القيمة الكلية:' : 'Total value:'}</span><strong>${y}</strong></div>
          <div class="kz-breakdown-row" style="border-top:1px solid var(--border-subtle); padding-block-start:var(--space-2); margin-block-start:var(--space-2);"><span>${isAr ? 'الناتج النهائي المستحق:' : 'Calculated Result:'}</span><strong style="color:var(--brand-primary); font-size:1.125rem;">${res.toString()}</strong></div>
        `;
      }
    } else if (currentMode === 'ratio') {
      const x = Number(byId('pct-ratio-x')?.value || 0);
      const y = Number(byId('pct-ratio-y')?.value || 0);
      if (y === 0) throw new Error(isAr ? 'لا يمكن القسمة على صفر.' : 'Division by zero is undefined.');

      const res = new Big(x).div(y).times(100);

      if (resultLabelEl) resultLabelEl.textContent = isAr ? `نسبة ${x} من ${y}` : `${x} as % of ${y}`;
      if (resultValEl) resultValEl.textContent = `${formatNumber(res.toNumber(), 2)}%`;
      if (resultSubEl) resultSubEl.textContent = isAr ? `النسبة المئوية المحسوبة` : `Calculated percentage`;
      if (formulaEl) formulaEl.textContent = `(${x} ÷ ${y}) × 100 = ${formatNumber(res.toNumber(), 2)}%`;
      if (breakdownWrap) {
        breakdownWrap.innerHTML = `
          <div class="kz-breakdown-row"><span>${isAr ? 'الجزء (X):' : 'Part (X):'}</span><strong>${x}</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'الكل (Y):' : 'Whole (Y):'}</span><strong>${y}</strong></div>
          <div class="kz-breakdown-row" style="border-top:1px solid var(--border-subtle); padding-block-start:var(--space-2); margin-block-start:var(--space-2);"><span>${isAr ? 'النسبة المئوية الممثلة:' : 'Percentage:'}</span><strong style="color:var(--brand-primary); font-size:1.125rem;">${formatNumber(res.toNumber(), 2)}%</strong></div>
        `;
      }
    } else if (currentMode === 'change') {
      const fromVal = Number(byId('pct-change-from')?.value || 0);
      const toVal = Number(byId('pct-change-to')?.value || 0);
      if (fromVal === 0) throw new Error(isAr ? 'القيمة الأصلية لا يمكن أن تكون صفراً لحساب التغير.' : 'Starting value cannot be zero.');

      const diff = new Big(toVal).minus(fromVal);
      const pctChange = diff.div(Math.abs(fromVal)).times(100);
      const isIncrease = diff.gte(0);

      const statusWord = isIncrease ? (isAr ? 'زيادة' : 'Increase') : (isAr ? 'انخفاض' : 'Decrease');

      if (resultLabelEl) resultLabelEl.textContent = `${statusWord}: ${Math.abs(pctChange.toNumber()).toFixed(2)}%`;
      if (resultValEl) resultValEl.textContent = `${isIncrease ? '+' : ''}${formatNumber(pctChange.toNumber(), 2)}%`;
      if (resultSubEl) resultSubEl.textContent = isAr ? `فارق القيمة: ${diff.toString()}` : `Absolute difference: ${diff.toString()}`;
      if (formulaEl) formulaEl.textContent = `((${toVal} - ${fromVal}) ÷ |${fromVal}|) × 100 = ${pctChange.toFixed(2)}%`;
      if (breakdownWrap) {
        breakdownWrap.innerHTML = `
          <div class="kz-breakdown-row"><span>${isAr ? 'القيمة الابتدائية:' : 'Initial Value:'}</span><strong>${fromVal}</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'القيمة الجديدة:' : 'New Value:'}</span><strong>${toVal}</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'الفارق العددي:' : 'Numeric Difference:'}</span><strong>${diff.toString()}</strong></div>
          <div class="kz-breakdown-row" style="border-top:1px solid var(--border-subtle); padding-block-start:var(--space-2); margin-block-start:var(--space-2);"><span>${isAr ? 'نسبة التغير:' : 'Percentage Change:'}</span><strong style="color:${isIncrease ? 'var(--brand-primary)' : 'var(--text-danger, #ef4444)'}; font-size:1.125rem;">${pctChange.toFixed(2)}% (${statusWord})</strong></div>
        `;
      }
    } else if (currentMode === 'discount') {
      const price = Number(byId('pct-disc-price')?.value || 0);
      const disc = Number(byId('pct-disc-rate')?.value || 0);

      const savings = new Big(price).times(disc).div(100);
      const finalPrice = new Big(price).minus(savings);

      if (resultLabelEl) resultLabelEl.textContent = isAr ? 'السعر النهائي بعد الخصم' : 'Final Price After Discount';
      if (resultValEl) resultValEl.textContent = formatNumber(finalPrice.toNumber(), 2);
      if (resultSubEl) resultSubEl.textContent = isAr ? `مقدار التوفير: ${formatNumber(savings.toNumber(), 2)}` : `Total Savings: ${formatNumber(savings.toNumber(), 2)}`;
      if (formulaEl) formulaEl.textContent = `${price} - (${price} × ${disc} ÷ 100) = ${finalPrice.toFixed(2)}`;
      if (breakdownWrap) {
        breakdownWrap.innerHTML = `
          <div class="kz-breakdown-row"><span>${isAr ? 'السعر الأصلي:' : 'Original Price:'}</span><strong>${price}</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'نسبة الخصم:' : 'Discount Rate:'}</span><strong>${disc}%</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'قيمة الخصم (المبلغ الموفر):' : 'Discount Amount (Savings):'}</span><strong style="color:var(--brand-primary);">${formatNumber(savings.toNumber(), 2)}</strong></div>
          <div class="kz-breakdown-row" style="border-top:1px solid var(--border-subtle); padding-block-start:var(--space-2); margin-block-start:var(--space-2);"><span>${isAr ? 'السعر الصافي المطلوب دفعه:' : 'Final Price:'}</span><strong style="font-size:1.125rem;">${formatNumber(finalPrice.toNumber(), 2)}</strong></div>
        `;
      }
    } else if (currentMode === 'reverse') {
      const x = Number(byId('pct-rev-x')?.value || 0);
      const y = Number(byId('pct-rev-y')?.value || 0);
      if (y === 0) throw new Error(isAr ? 'النسبة لا يمكن أن تكون صفراً.' : 'Percentage cannot be zero.');

      const total = new Big(x).div(y).times(100);

      if (resultLabelEl) resultLabelEl.textContent = isAr ? `العدد الإجمالي الكامل (100%)` : `Total Whole (100%)`;
      if (resultValEl) resultValEl.textContent = formatNumber(total.toNumber(), 4);
      if (resultSubEl) resultSubEl.textContent = isAr ? `إذا كان ${x} يمثل ${y}%` : `When ${x} represents ${y}%`;
      if (formulaEl) formulaEl.textContent = `(${x} ÷ ${y}) × 100 = ${total.toString()}`;
      if (breakdownWrap) {
        breakdownWrap.innerHTML = `
          <div class="kz-breakdown-row"><span>${isAr ? 'القيمة الجزئية:' : 'Partial Value:'}</span><strong>${x}</strong></div>
          <div class="kz-breakdown-row"><span>${isAr ? 'النسبة التي تمثلها:' : 'Percentage it represents:'}</span><strong>${y}%</strong></div>
          <div class="kz-breakdown-row" style="border-top:1px solid var(--border-subtle); padding-block-start:var(--space-2); margin-block-start:var(--space-2);"><span>${isAr ? 'الإجمالي الكلي (100%):' : 'Total 100%:'}</span><strong style="color:var(--brand-primary); font-size:1.125rem;">${total.toString()}</strong></div>
        `;
      }
    }
  } catch (err) {
    if (resultValEl) resultValEl.textContent = '--';
    if (resultSubEl) resultSubEl.textContent = err.message || (isAr ? 'مدخلات غير صالحة' : 'Invalid input');
    if (breakdownWrap) breakdownWrap.innerHTML = '';
  }
}

export function initPercentageCalculator() {
  const container = byId('percentage-calculator-app');
  if (!container) return;

  // Mode segmented switcher
  setupSegmented('pct-mode-toggle', (mode) => {
    currentMode = mode;
    ['part_of_whole', 'ratio', 'change', 'discount', 'reverse'].forEach((m) => {
      const panel = byId(`panel-${m}`);
      if (panel) panel.style.display = m === mode ? 'block' : 'none';
    });
    calculate();
    trackEvent('tool_interacted', 'percentage-calculator', { mode });
  });

  // Setup listeners for all inputs
  const allInputIds = [
    'pct-x', 'pct-y',
    'pct-ratio-x', 'pct-ratio-y',
    'pct-change-from', 'pct-change-to',
    'pct-disc-price', 'pct-disc-rate',
    'pct-rev-x', 'pct-rev-y'
  ];

  allInputIds.forEach((id) => {
    const el = byId(id);
    if (el) {
      el.addEventListener('input', calculate);
      el.addEventListener('change', calculate);
    }
  });

  // Preset chips for percentage
  document.querySelectorAll('.pct-preset-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      const val = chip.dataset.pct;
      if (currentMode === 'part_of_whole') {
        const inp = byId('pct-x');
        if (inp) { inp.value = val; calculate(); }
      } else if (currentMode === 'discount') {
        const inp = byId('pct-disc-rate');
        if (inp) { inp.value = val; calculate(); }
      }
    });
  });

  attachCopyButton(byId('pct-copy-btn'), () => {
    const label = byId('pct-result-label')?.textContent || '';
    const val = byId('pct-result-val')?.textContent || '';
    const formula = byId('pct-formula')?.textContent || '';
    return isAr
      ? `📋 نتيجة حساب النسبة المئوية:\n• العملية: ${label}\n• النتيجة: ${val}\n• المعادلة: ${formula}`
      : `📋 Percentage Calculation Result:\n• Operation: ${label}\n• Result: ${val}\n• Formula: ${formula}`;
  });

  const printBtn = byId('pct-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  calculate();
  trackEvent('tool_started', 'percentage-calculator');
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPercentageCalculator);
  } else {
    initPercentageCalculator();
  }
}
